import { useState } from "react";
import EmployeeLayout from "../../layouts/EmployeeLayout";
import { motion } from "framer-motion";
import { createEmployeeActivity } from "../../api/employeeService";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────
   EmployeeActivity — log daily activities
───────────────────────────────────────────── */
function EmployeeActivity() {

  const navigate = useNavigate();
  const [form, setForm] = useState({
    activity_name: "",
    activity_date: "",
    description: "",
    duration_hours: "",
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");
    try {
      const payload = {
        activity_name: form.activity_name,
        activity_date: form.activity_date,
        description: form.description,
        duration_hours: parseFloat(form.duration_hours),
      };
      const res = await createEmployeeActivity(payload);
      if (res.data?.success) {
        setSuccessMsg("✅ Activity logged successfully");
        setForm({
          activity_name: "",
          activity_date: "",
          description: "",
          duration_hours: "",
        });
      }
    } catch (err) {
      console.error(err);
      setErrorMsg(
        err?.response?.data?.error || "Failed to log activity. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <EmployeeLayout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* ── HEADER ── */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto mb-6 sm:mb-8"
          >
            <div className="flex items-center justify-between w-full gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl sm:text-3xl">🗂️</span>
                </div>
                <div>
                  <h1 className="text-xl sm:text-3xl font-bold text-white">
                    Employee Activity
                  </h1>
                  <p className="text-white/60 text-xs sm:text-sm">
                    Log your daily work activities
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/employee/activity/progress")}
                className="flex-shrink-0 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs sm:text-sm font-semibold transition-all shadow"
              >
                📊 Activity Progress
              </button>
            </div>
          </motion.div>

          {/* ── FORM CARD ── */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-5 sm:p-10 max-w-3xl mx-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">

              <InputField
                label="Activity Name"
                name="activity_name"
                value={form.activity_name}
                onChange={handleChange}
                placeholder="Enter activity name"
                required
              />

              <InputField
                label="Activity Date"
                type="date"
                name="activity_date"
                value={form.activity_date}
                onChange={handleChange}
                required
              />

              <div>
                <label className="block text-white font-semibold mb-2 text-sm">
                  Description
                </label>
                <textarea
                  name="description"
                  rows="4"
                  placeholder="Optional description..."
                  value={form.description}
                  onChange={handleChange}
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all resize-none"
                />
              </div>

              <InputField
                label="Duration (hours)"
                type="number"
                step="0.25"
                name="duration_hours"
                value={form.duration_hours}
                onChange={handleChange}
                placeholder="e.g. 2.5"
                required
              />

              {successMsg && (
                <p className="text-green-300 text-sm font-semibold bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3">
                  {successMsg}
                </p>
              )}
              {errorMsg && (
                <p className="text-red-300 text-sm font-semibold bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  {errorMsg}
                </p>
              )}

              <div className="flex justify-center pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-10 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold text-sm transition-all shadow-lg hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
                >
                  {loading ? "Saving..." : "Create Activity"}
                </button>
              </div>
            </form>
          </motion.div>

        </div>
      </div>
    </EmployeeLayout>
  );
}

/* ─────────────────────────────────────────────
   Reusable input field
───────────────────────────────────────────── */
function InputField({
  label,
  type = "text",
  name,
  value,
  onChange,
  placeholder,
  required,
  step,
}) {
  return (
    <div>
      <label className="block text-white font-semibold mb-2 text-sm">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        step={step}
        className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
      />
    </div>
  );
}

export default EmployeeActivity;